import { useState } from 'react';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<Size, string> = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
  xl: 'h-14 w-14 text-base',
};

interface AvatarProps {
  src?:       string | null;
  alt?:       string;
  username?:  string;
  size?:      Size;
  className?: string;
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/[\s_-]+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// Deterministic hue from username string
function hue(name?: string): number {
  if (!name) return 180;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}

export default function Avatar({ src, alt, username, size = 'md', className = '' }: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const h = hue(username);

  const base = `inline-flex shrink-0 items-center justify-center rounded-full font-mono font-semibold overflow-hidden ${SIZE[size]} ${className}`;

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={alt ?? username ?? 'avatar'}
        className={`${base} object-cover`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <span
      className={base}
      style={{
        background: `hsl(${h},40%,20%)`,
        color:      `hsl(${h},60%,65%)`,
        border:     `1px solid hsl(${h},40%,30%)`,
      }}
      aria-label={alt ?? username ?? 'avatar'}
    >
      {initials(username)}
    </span>
  );
}
